import { createRoot } from 'react-dom/client';
import { Button } from '../../../ui/kit/components/primitives';
import { Modal } from '../../../ui/kit/components/overlays';
import { quoteInr } from './ledger';
import '../../../ui/styles/components.css';

export interface CostQuote {
  token: string;
  operation: string;
  projectName?: string;
  expiresAt: number;
  approvedUpperInr?: number;
  price: { inr: number; upperInr?: number; usd?: number; externalCredits?: boolean; model: string; provider: string; note?: string; source: string; overTarget?: { byINR: number; targetINR: number } | null; expensive?: boolean };
}

let queue: Promise<unknown> = Promise.resolve();

export function isFreeQuote(quote: CostQuote): boolean {
  const price = quote?.price;
  if (!price || price.externalCredits) return false;
  const upper = Number(quote.approvedUpperInr ?? price.upperInr ?? price.inr);
  return Number.isFinite(upper) && upper === 0 && Number(price.inr || 0) === 0;
}

interface ArmedCeiling { ceilingInr: number; operations: string[]; basis: 'upper' | 'expected'; uses: number; onMoved?: (nextInr: number) => void }
let armed: ArmedCeiling | null = null;

const formatRupees = (value: number) => {
  const amount = Number(value) || 0;
  const digits = amount >= 100 ? 0 : 2;
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
};

export async function withPricedCeiling<T>(ceilingInr: number, run: () => Promise<T>, { operations = [], basis = 'upper', uses = 1, onMoved }: { operations?: string[]; basis?: 'upper' | 'expected'; uses?: number; onMoved?: (nextInr: number) => void } = {}): Promise<T> {
  const previous = armed;
  armed = Number(ceilingInr) > 0 ? { ceilingInr: Number(ceilingInr), operations, basis, uses, onMoved } : null;
  try {
    return await run();
  } finally {
    armed = previous;
  }
}

function priceAgainstCeiling(quote: CostQuote, ceiling: ArmedCeiling): boolean {
  const price = ceiling.basis === 'expected' ? Number(quote.price?.inr) : Number(quote.approvedUpperInr ?? quote.price?.upperInr ?? quote.price?.inr);
  if (Number.isFinite(price) && price <= ceiling.ceilingInr + 0.01) {
    ceiling.uses -= 1;
    return true;
  }
  ceiling.uses = 0;
  ceiling.onMoved?.(price);
  throw Object.assign(new Error(`Price changed ${formatRupees(ceiling.ceilingInr)} → ${formatRupees(price)}. Tap again.`), { code: 'COST_REQUOTE', quote, previousUpperInr: ceiling.ceilingInr, nextInr: price });
}

export function confirmCost(quote: CostQuote): Promise<boolean> {
  if (isFreeQuote(quote)) return Promise.resolve(Date.now() < quote.expiresAt);
  const ceiling = armed;
  if (ceiling && ceiling.uses > 0 && (!ceiling.operations.length || ceiling.operations.includes(quote.operation))) {
    try { return Promise.resolve(priceAgainstCeiling(quote, ceiling) && Date.now() < quote.expiresAt); } catch (error) { return Promise.reject(error); }
  }
  const requestLocation = location.href;
  if (import.meta.env?.DEV) console.warn(`Unpriced paid call: ${quote.operation}`);

  const result = queue.then(() => showCost(quote, requestLocation));
  queue = result.catch(() => false);
  return result;
}

function showCost(quote: CostQuote, requestLocation: string): Promise<boolean> {
  const valid = () => Number.isFinite(quote.expiresAt) && Date.now() < quote.expiresAt && location.href === requestLocation;
  if (!valid()) return Promise.resolve(false);
  return new Promise(resolve => {
    const host = document.createElement('div');
    host.className = 'bs-theme';
    (document.fullscreenElement || document.body).append(host);
    const root = createRoot(host);
    const restoreFocus = document.activeElement as HTMLElement | null;
    let done = false;
    const finish = (accepted: boolean) => {
      if (done) return;
      done = true;
      const approved = accepted && valid();
      clearInterval(timer);
      root.unmount();
      host.remove();
      restoreFocus?.focus();
      resolve(approved);
    };
    const timer = setInterval(() => { if (!valid()) finish(false); }, 250);
    const currency = quoteInr;
    const { price } = quote;
    root.render(<Modal open onOpenChange={() => finish(false)} title={quote.operation} description="Nothing has started. This approval covers only the request below." footer={<><Button onClick={() => finish(false)}>Cancel</Button><Button variant="primary" onClick={() => finish(true)}>Approve & run</Button></>}>
      {quote.projectName && <p>{quote.projectName}</p>}
      <h3>{price.externalCredits ? 'Billed in Magnific credits' : `${currency(price.inr)}${price.upperInr && price.upperInr > price.inr ? ` – ${currency(price.upperInr)}` : ''} estimated`}</h3>
      <p>{price.model} · {price.provider}</p>
      {price.note && <p>{price.note}</p>}
      <p className="bs-muted bs-small">Rate source: {price.source}. Valid until {new Date(quote.expiresAt).toLocaleTimeString()}.</p>
    </Modal>);
  });
}
